// @flow

import React, { useCallback, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { buttonDefault, Color, flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { MarketsHero, ResourcesSideBar } from 'components/Content';
import { Button, ImageLoader } from 'components/Gui';
import axios from 'axios';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
`;

const Content = styled.div`
  width: 100%;
  background: white;

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex()};
    flex-direction: column;

    @media (min-width: ${Width.BREAK_MD}px) {
      ${flex('flex-start', 'flex-start')};
      flex-direction: row;
    }
  }
`;

const Blurb = styled.div`
  width: 100%;
  background: white;
  padding-bottom: 40px;

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex('flex-start', 'flex-start')};
    flex-direction: column;

    div.form {
      width: 100%;
      ${flex('flex-start', 'flex-start')};
      margin-top: 40px;

      div.image {
        width: 260px;
        flex: 0 0 auto;
        display: none;

        @media (min-width: ${Width.BREAK_MD}px) {
          display: block;
        }
      }

      div.inputs {
        width: 100%;
        flex: 0 0 auto;

        @media (min-width: ${Width.BREAK_MD}px) {
          width: 460px;
        }

        > div {
          &:first-of-type {
            ${flex('flex-start', 'flex-start')};
            flex-wrap: wrap;

            input {
              width: 200px;
              margin-right: 20px;
              ${buttonDefault};
              font-weight: 400;
              font-size: 2rem;
              margin-bottom: 20px;
              border-bottom: 1px solid ${Color.BLACK_SOUL};
            }

            textarea {
              width: 420px;
              ${buttonDefault};
              font-weight: 400;
              font-size: 2rem;
              margin-bottom: 20px;
              margin-top: 20px;
              height: 100px;
              border-bottom: 1px solid ${Color.BLACK_SOUL};
            }
          }
        }
      }
    }
  }
`;

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);
  const [formData, setFormData] = useState({});
  const onSubmit = useCallback(() => {
    const go = async () => {
      if (formData.firstName && formData.lastName && formData.email && formData.inquiry) {
        await axios.post('http://54.174.87.76/api/message/markets_direct', {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          language: 'en',
          inquiry: formData.inquiry,
        });
      }
    };
    go();
  }, [formData]);
  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('contact-us'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <MarketsHero
          title="CONTACT US"
          blurb="Questions? We're here to help"
          image={{
            name: 'contact-us',
          }}
          images={data}
        />
        <Content>
          <ResourcesSideBar sectionIndex={2}>
            <Blurb>
              <div>
                <h2>Get in Touch</h2>
                <p>
                  We'd love to chat! Just give us a little information and someone from our team will be in touch as soon as possible.
                </p>
                <div className="form">
                  <div className="inputs">
                    <div>
                      <input
                        onChange={({ target: { value } }) =>
                          setFormData((data) => ({
                            ...data,
                            firstName: value,
                          }))
                        }
                        placeholder="First Name"
                        type="text"
                        name="fname"
                        autoComplete={`given-name`}
                      />
                      <input
                        onChange={({ target: { value } }) =>
                          setFormData((data) => ({
                            ...data,
                            lastName: value,
                          }))
                        }
                        placeholder="Last Name"
                        type="text"
                        name="lname"
                        autoComplete={`family-name`}
                      />
                      <input
                        onChange={({ target: { value } }) =>
                          setFormData((data) => ({
                            ...data,
                            email: value,
                          }))
                        }
                        placeholder="Email"
                        type="text"
                        name={`email`}
                        autoComplete={`email`}
                      />
                      <textarea
                        onChange={({ target: { value } }) =>
                          setFormData((data) => ({
                            ...data,
                            inquiry: value,
                          }))
                        }
                        placeholder="Inquiry Details"
                      />
                    </div>
                    <Button className="button" onClick={onSubmit}>
                      Submit
                    </Button>
                  </div>
                  <div className="image">
                    <ImageLoader image={getImage('contact-hero')} />
                  </div>
                </div>
              </div>
            </Blurb>
          </ResourcesSideBar>
        </Content>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    imageToWebP: allFile(filter: { relativeDirectory: { eq: "pages/contact-us" }, extension: { nin: ["svg", "gif"] } }) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/contact-us" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
